import { Game, Player, Vote, User } from "@/types";
import { getSessionId } from "@/utils/storage";

// Sample players
const PLAYERS: Player[] = [
  { id: "p1", name: "Alex Johnson", number: 7, position: "Forward", team_id: "team1" },
  { id: "p2", name: "Sam Williams", number: 10, position: "Midfielder", team_id: "team1" },
  { id: "p3", name: "Jordan Lee", number: 4, position: "Defender", team_id: "team1" },
  { id: "p4", name: "Taylor Chen", number: 9, position: "Forward", team_id: "team1" },
  { id: "p5", name: "Morgan Smith", number: 6, position: "Midfielder", team_id: "team1" },
  { id: "p6", name: "Casey Brown", number: 2, position: "Defender", team_id: "team1" },
  { id: "p7", name: "Riley Wilson", number: 1, position: "Goalkeeper", team_id: "team1" },
  { id: "p8", name: "Quinn Martinez", number: 5, position: "Defender", team_id: "team1" },
  { id: "p9", name: "Avery Davis", number: 8, position: "Midfielder", team_id: "team1" },
  { id: "p10", name: "Cameron White", number: 11, position: "Forward", team_id: "team1" },
];

// Sample games
const GAMES: Game[] = [
  {
    id: "g1",
    date: "2023-10-15",
    opponent: "United FC",
    voting_open: true,
    created_at: "2023-10-14T15:00:00Z",
    team_id: "team1"
  },
  {
    id: "g2",
    date: "2023-10-22",
    opponent: "City Rangers",
    voting_open: true,
    created_at: "2023-10-21T15:00:00Z",
    team_id: "team1"
  },
  {
    id: "g3",
    date: "2023-10-29",
    opponent: "Athletic Club",
    voting_open: true,
    created_at: "2023-10-28T15:00:00Z",
    team_id: "team1"
  }
];

// Sample votes
let VOTES: Vote[] = [
  {
    id: "v1",
    game_id: "g1",
    player_id: "p1",
    points: 3,
    voter_session_id: "session1",
    created_at: "2023-10-15T18:00:00Z"
  },
  {
    id: "v2",
    game_id: "g1",
    player_id: "p2",
    points: 2,
    voter_session_id: "session1",
    created_at: "2023-10-15T18:00:00Z"
  },
  {
    id: "v3",
    game_id: "g1",
    player_id: "p3",
    points: 1,
    voter_session_id: "session1",
    created_at: "2023-10-15T18:00:00Z"
  },
  {
    id: "v4",
    game_id: "g1",
    player_id: "p1",
    points: 3,
    voter_session_id: "session2",
    created_at: "2023-10-15T19:00:00Z"
  }
];

// Sample users
const USERS: User[] = [
  {
    id: "u1",
    email: "coach@example.com",
    role: "coach"
  }
];

// Simulate API delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// API functions
export const getPlayers = async (): Promise<Player[]> => {
  await delay(500);
  return [...PLAYERS];
};

export const getGames = async (): Promise<Game[]> => {
  await delay(500);
  return [...GAMES];
};

export const getGame = async (id: string): Promise<Game | null> => {
  await delay(300);
  return GAMES.find(game => game.id === id) || null;
};

export const createGame = async (game: Omit<Game, "id" | "created_at" | "team_id">): Promise<Game> => {
  await delay(800);
  const newGame: Game = {
    ...game,
    id: `g${GAMES.length + 1}`,
    created_at: new Date().toISOString(),
    team_id: "team1" // Default team ID for mock data
  };
  
  GAMES.push(newGame);
  return newGame;
};

export const getGameVotes = async (gameId: string): Promise<Vote[]> => {
  await delay(500);
  return VOTES.filter(vote => vote.game_id === gameId);
};

export const submitVotes = async (
  gameId: string, 
  votes: { playerId: string; points: number }[]
): Promise<Vote[]> => {
  await delay(800);
  
  const sessionId = getSessionId();
  const newVotes: Vote[] = votes.map((vote, index) => ({
    id: `v${VOTES.length + index + 1}`,
    game_id: gameId,
    player_id: vote.playerId,
    points: vote.points,
    voter_session_id: sessionId,
    created_at: new Date().toISOString()
  }));
  
  VOTES = [...VOTES, ...newVotes];
  return newVotes;
};

export const getPlayerStats = async (): Promise<{ 
  playerId: string; 
  player: Player; 
  totalPoints: number; 
  gameBreakdown: { gameId: string; points: number }[];
}[]> => {
  await delay(800);
  
  return PLAYERS.map(player => {
    const playerVotes = VOTES.filter(vote => vote.player_id === player.id);
    const totalPoints = playerVotes.reduce((sum, vote) => sum + vote.points, 0);
    
    const gameBreakdown = GAMES.map(game => {
      const gameVotes = playerVotes.filter(vote => vote.game_id === game.id);
      const points = gameVotes.reduce((sum, vote) => sum + vote.points, 0);
      return { gameId: game.id, points };
    });
    
    return { playerId: player.id, player, totalPoints, gameBreakdown };
  }).sort((a, b) => b.totalPoints - a.totalPoints);
};

export const authenticateUser = async (email: string, password: string): Promise<User | null> => {
  await delay(500);
  
  // For demo purposes, any coach@example.com with password "coach123" works
  if (email === "coach@example.com" && password === "coach123") {
    return USERS.find(user => user.email === email) || null;
  }
  
  return null;
};

export const toggleGameVoting = async (gameId: string, isOpen: boolean): Promise<Game> => {
  await delay(500);
  
  const gameIndex = GAMES.findIndex(game => game.id === gameId);
  if (gameIndex === -1) throw new Error("Game not found");
  
  GAMES[gameIndex] = { ...GAMES[gameIndex], voting_open: isOpen };
  return GAMES[gameIndex];
};
